import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';

const AVATAR_SIZE = 30;
const AVATAR_ICON_SIZE = 16;

// Speech-bubble corner radii: a deliberate, slightly-rounder-than-default bubble with a
// squared "tail" corner toward the speaker. Kept as named constants (not THEME tokens) because
// the bubble shape is a specific design requirement, not the standard 8px card radius.
const BUBBLE_RADIUS = 14;
const BUBBLE_TAIL = THEME.spacing.xs; // 4px

/**
 * Styles for `TranscriptMessage`, modelling the AI Coaching Review chat-bubble design.
 *
 * Each turn is an avatar + a rounded bubble. The Rep (the human on the call) sits on the right with
 * a tinted primary bubble, mirroring the "me" side of a chat thread; the AI Coach sits on the left
 * on the neutral surface. `System` turns are a centered divider rather than a bubble. Colors and
 * spacing come exclusively from `THEME` tokens so the surface re-themes with the rest of the app.
 */
export const transcriptMessageStyles = StyleSheet.create({
	row: {
		alignItems: 'flex-end',
		display: 'flex',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.md,
	},
	// Rep turns flip the avatar/bubble order so the Rep reads on the right of the thread.
	rowRep: {
		flexDirection: 'row-reverse',
	},
	avatar: {
		alignItems: 'center',
		borderRadius: '50%',
		color: THEME.colors.background.light,
		display: 'flex',
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		height: AVATAR_SIZE,
		justifyContent: 'center',
		width: AVATAR_SIZE,
	},
	avatarCoach: {
		background: `linear-gradient(135deg, ${THEME.colors.primary.main}, ${THEME.colors.tertiary.dark})`,
	},
	avatarRep: {
		background: `linear-gradient(135deg, ${THEME.colors.chart.levBlue}, ${THEME.colors.chart.abracadabra})`,
	},
	avatarIcon: {
		height: AVATAR_ICON_SIZE,
		width: AVATAR_ICON_SIZE,
	},
	bubble: {
		backgroundColor: THEME.colors.background.main,
		border: THEME.common.border,
		// Asymmetric radius tucks the bottom-left corner toward the AI Coach avatar.
		borderRadius: `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_TAIL}px`,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		maxWidth: '76%',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	bubbleRep: {
		backgroundColor: THEME.colors.primary.light,
		borderColor: 'transparent',
		// Mirror the tuck toward the Rep avatar on the right.
		borderRadius: `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px ${BUBBLE_TAIL}px ${BUBBLE_RADIUS}px`,
	},
	header: {
		alignItems: 'baseline',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	headerRep: {
		flexDirection: 'row-reverse',
	},
	roleChip: {
		borderRadius: 9999,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		padding: `2px ${THEME.spacing.sm}px`,
	},
	roleChipCoach: {
		backgroundColor: THEME.colors.pill.teal.background,
		color: THEME.colors.pill.teal.icon,
	},
	roleChipRep: {
		backgroundColor: THEME.colors.pill.blue.background,
		color: THEME.colors.pill.blue.icon,
	},
	timestamp: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xxs,
	},
	text: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	emptyText: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontStyle: 'italic',
	},
	tokens: {
		alignItems: 'baseline',
		color: THEME.colors.text.light,
		display: 'flex',
		flexWrap: 'wrap',
		fontSize: THEME.typography.fontSizes.xxs,
		gap: THEME.spacing.sm,
	},
	model: {
		color: THEME.colors.text.light,
		fontStyle: 'italic',
	},
	feedbackToolbar: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.xs,
		justifyContent: 'flex-end',
		marginTop: THEME.spacing.xs,
	},
	feedbackButton: {
		color: THEME.colors.text.light,
		height: 24,
		width: 24,
		':hover': {
			color: THEME.colors.text.main,
		},
	},
	feedbackButtonUpActive: {
		backgroundColor: THEME.colors.pill.green.background,
		color: THEME.colors.pill.green.icon,
	},
	feedbackButtonDownActive: {
		backgroundColor: THEME.colors.pill.red.background,
		color: THEME.colors.pill.red.icon,
	},
	// System turns: a centered "rule — pill — rule" divider rather than a chat bubble.
	system: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		gap: THEME.spacing.md,
		marginBottom: THEME.spacing.md,
	},
	systemRule: {
		backgroundColor: THEME.colors.border.light,
		flex: 1,
		height: 1,
	},
	systemText: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		border: THEME.common.border,
		borderRadius: 9999,
		display: 'inline-flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.xs,
		padding: `3px ${THEME.spacing.md}px`,
	},
});
